import React, { useMemo } from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { OptionType } from "commons/ui/input/Select";

import { notEmptyValidator } from "commons/utils/validate";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useMaterialApi from "hooks/api/private/useMaterialApi";
import useMaterialCategoryApi from "hooks/api/private/useMaterialCategoryApi";
import useMaterialSubcategoryApi from "hooks/api/private/useMaterialSubcategoryApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";

import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { MaterialBody, MaterialDetails } from "types/api/material";
import { MaterialCategoryListItem } from "types/api/materialCategory";
import { MaterialSubcategoryListItem } from "types/api/materialSubcategory";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType, emptyListRequest } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type MaterialBodyForm = {
    type: string | null;
    material_category_id: string | null;
    material_subcategory_id: string | null;
    enabled: boolean;
    graphical_order: number | null;
    article: string;
    code: string;
    cat: string;
    thumb_img: DesktopMobileForm;
    main_img: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            art_rr: string;
            code_rr: string;
            description: string;
            maintenance: string;
            disclaimer: string;
        };
    };
};

export type MaterialFormHook = {
    formHook: FormHookType<MaterialBodyForm>;
    setThumbImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setMainImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    categoryOptions: OptionType[];
    subcategoryOptions: OptionType[];
    typeOptions: OptionType[];
};

function useMaterialForm({ materialId }: { materialId?: string }): MaterialFormHook {
    const { createMaterial, updateMaterial, getOneMaterial } = useMaterialApi();
    const { getAllMaterialCategory } = useMaterialCategoryApi();
    const { getAllMaterialSubcategory } = useMaterialSubcategoryApi();

    const { addSaveAction } = useSaveActionsContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                type: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                material_category_id: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                material_subcategory_id: {
                    type: "simple",
                    validators: [],
                },
                enabled: {
                    type: "simple",
                    validators: [],
                },
                graphical_order: {
                    type: "simple",
                    validators: [],
                },
                article: {
                    type: "simple",
                    validators: [],
                },
                code: {
                    type: "simple",
                    validators: [],
                },
                cat: {
                    type: "simple",
                    validators: [],
                },
                thumb_img: notEmptyMedia,
                main_img: notEmptyMedia,
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            art_rr: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            code_rr: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            description: {
                                type: "simple",
                                validators: [],
                            },
                            maintenance: {
                                type: "simple",
                                validators: [],
                            },
                            disclaimer: {
                                type: "simple",
                                validators: [],
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: MaterialBodyForm = React.useMemo(() => {
        return {
            type: null,
            material_category_id: null,
            material_subcategory_id: null,
            enabled: true,
            graphical_order: null,
            article: "",
            code: "",
            cat: "",
            thumb_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            main_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: { art_rr: "", code_rr: "", description: "", maintenance: "", disclaimer: "" },
                en: { art_rr: "", code_rr: "", description: "", maintenance: "", disclaimer: "" },
            },
        };
    }, []);

    const formHook = useForm<MaterialBodyForm>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const { setDesktopMobileImage: setThumbImage } = useMobileOrDesktopFile({
        setForm,
        path: "thumb_img",
    });

    const { setDesktopMobileImage: setMainImage } = useMobileOrDesktopFile({
        setForm,
        path: "main_img",
    });


    const [allCategoryOptions, setAllCategoryOptions] = React.useState<MaterialCategoryListItem[]>([]);

    const [categoryOptions, setCategoryOptions] = React.useState<OptionType[]>([]);
    const [subcategoryOptions, setSubcategoryOptions] = React.useState<OptionType[]>([]);

    const typeOptions = useMemo(() => {
        return [
            { value: "", label: "-" },
            { value: "indoor", label: "Indoor" },
            { value: "outdoor", label: "Outdoor" },
        ];
    }, []);


    React.useEffect(() => {
        const [apiCall, abortController] = getAllMaterialCategory(emptyListRequest);

        apiCall.then((response) => {
            setAllCategoryOptions(response.content);
        });

        return () => abortController.abort();
    }, [getAllMaterialCategory]);

    React.useEffect(() => {
        if (form.type) {
            const filteredCategories = allCategoryOptions.filter((c) => c.type === form.type);

            setCategoryOptions([
                { value: "", label: "-" },
                ...filteredCategories.map((c: MaterialCategoryListItem) => ({ value: c.id, label: c.name })),
            ]);
        } else {
            setCategoryOptions([
                { value: "", label: "-" },
            ]);
        }
    }, [allCategoryOptions, form.type]);

    React.useEffect(() => {
        const [apiCall, abortController] = getAllMaterialSubcategory(emptyListRequest);

        apiCall.then((response) => {
            setSubcategoryOptions([
                { value: "", label: "-" },
                ...response.content.map((c: MaterialSubcategoryListItem) => ({ value: c.id, label: c.name })),
            ]);
        });

        return () => abortController.abort();
    }, [getAllMaterialSubcategory]);

    const [details, setDetails] = React.useState<MaterialDetails | undefined>(undefined);

    React.useEffect(() => {
        if (materialId) {
            const [apiCall, abortController] = getOneMaterial({ id: materialId });

            apiCall.then(setDetails);

            return () => abortController.abort();
        }
    }, [getOneMaterial, materialId]);

    React.useEffect(() => {
        if (details) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.type = details.type;
                newForm.material_category_id = details.material_category_id;
                newForm.material_subcategory_id = details.material_subcategory_id;
                newForm.enabled = details.enabled;
                newForm.graphical_order = details.graphical_order;
                newForm.article = details.article ?? "";
                newForm.code = details.code ?? "";
                newForm.cat = details.cat ?? "";

                newForm.text.it.art_rr = details.text.it.art_rr ?? "";
                newForm.text.it.code_rr = details.text.it.code_rr ?? "";
                newForm.text.it.description = details.text.it.description ?? "";
                newForm.text.it.maintenance = details.text.it.maintenance ?? "";
                newForm.text.it.disclaimer = details.text.it.disclaimer ?? "";

                newForm.text.en.art_rr = details.text.en.art_rr ?? "";
                newForm.text.en.code_rr = details.text.en.code_rr ?? "";
                newForm.text.en.description = details.text.en.description ?? "";
                newForm.text.en.maintenance = details.text.en.maintenance ?? "";
                newForm.text.en.disclaimer = details.text.en.disclaimer ?? "";

                if (details.thumb_img) {
                    newForm.thumb_img = {
                        id: details.thumb_img.id,
                        desktop: createAttachmentType({
                            id: details.thumb_img.desktop?.id ?? null,
                            label: "Desktop",
                            filename: details.thumb_img.desktop?.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: details.thumb_img.mobile?.id ?? null,
                            label: "Mobile",
                            filename: details.thumb_img.mobile?.filename,
                            status: 2,
                        }),
                    };
                }

                if (details.main_img) {
                    newForm.main_img = {
                        id: details.main_img.id,
                        desktop: createAttachmentType({
                            id: details.main_img.desktop?.id ?? null,
                            label: "Desktop",
                            filename: details.main_img.desktop?.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: details.main_img.mobile?.id ?? null,
                            label: "Mobile",
                            filename: details.main_img.mobile?.filename,
                            status: 2,
                        }),
                    };
                }

                return newForm;
            });
        }
    }, [setForm, details]);

    const body: MaterialBody = React.useMemo(() => {
        return {
            material_category_id: form.material_category_id || null,
            material_subcategory_id: form.material_subcategory_id || null,
            enabled: form.enabled,
            graphical_order: form.graphical_order ?? 0,
            article: form.article,
            code: form.code,
            cat: form.cat,
            thumb_img: {
                id: form.thumb_img.id,
                desktop: converterAttachmentToFileWithPayload(form.thumb_img.desktop),
                mobile: converterAttachmentToFileWithPayload(form.thumb_img.mobile),
            },
            main_img: {
                id: form.main_img.id,
                desktop: converterAttachmentToFileWithPayload(form.main_img.desktop),
                mobile: converterAttachmentToFileWithPayload(form.main_img.mobile),
            },
            text: {
                it: {
                    art_rr: form.text.it.art_rr,
                    code_rr: form.text.it.code_rr,
                    description: form.text.it.description,
                    maintenance: form.text.it.maintenance,
                    disclaimer: form.text.it.disclaimer,
                },
                en: {
                    art_rr: form.text.en.art_rr,
                    code_rr: form.text.en.code_rr,
                    description: form.text.en.description,
                    maintenance: form.text.en.maintenance,
                    disclaimer: form.text.en.disclaimer,
                },
            },
        };
    }, [form]);

    React.useEffect(() => {
        addSaveAction("useMaterialForm", () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                if (materialId) {
                    const [apiCall] = updateMaterial({ id: materialId }, body);
                    promises.push(apiCall);
                } else {
                    const [apiCall] = createMaterial(body);
                    promises.push(apiCall);
                }

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        addSaveAction,
        showAllError,
        createMaterial,
        updateMaterial,
        body,
        materialId,
        isValid,
    ]);

    return React.useMemo(
        (): MaterialFormHook => ({
            formHook,
            setThumbImage,
            setMainImage,
            categoryOptions,
            subcategoryOptions,
            typeOptions,
        }),
        [formHook, setThumbImage, setMainImage, categoryOptions, subcategoryOptions, typeOptions],
    );
}

export default useMaterialForm;
